import { useEffect, useRef, useCallback } from 'react';
import { apiHomeTodoList } from '@/api/home';
import { IHomeTodoResponse } from '@/api/typings/home';
import LogoImg from '@/assets/logo.png';
import { useStore } from '@/store';
import {
  PerformanceMonthStageText,
  PerformanceStage,
  PerformanceStageText,
  PerformanceType,
} from '@/constants/business/performance';
import {
  AuditBusinessCode,
  AuditBusinessCodeText,
} from '@/constants/business/audit';

const POLL_INTERVAL = 10 * 60 * 1000; // 测试环境改成5 分钟
const TIME_WINDOW = POLL_INTERVAL / 1000;
const DISMISSED_NOTIFICATION_STORAGE_KEY = 'ehr_todo_notification_dismissed_v1';
const MAX_DISMISSED_NOTIFICATION_COUNT = 500;
const DISMISSED_NOTIFICATION_EXPIRE_SECONDS = 7 * 24 * 60 * 60;

interface INotificationTodo {
  title: string;
  content: string;
  url: string;
  ctime?: number;
}

function buildNotificationKey(item: INotificationTodo): string {
  return `${item.url}__${item.ctime || 0}__${item.title}`;
}

function cleanupDismissedNotificationMap(
  dismissedMap: Map<string, number>,
  now = Math.floor(Date.now() / 1000)
) {
  dismissedMap.forEach((dismissedAt, key) => {
    if (now - dismissedAt > DISMISSED_NOTIFICATION_EXPIRE_SECONDS) {
      dismissedMap.delete(key);
    }
  });

  const overflow = dismissedMap.size - MAX_DISMISSED_NOTIFICATION_COUNT;
  if (overflow <= 0) return;

  Array.from(dismissedMap.entries())
    .sort((a, b) => a[1] - b[1])
    .slice(0, overflow)
    .forEach(([key]) => dismissedMap.delete(key));
}

function getDismissedNotificationMap(): Map<string, number> {
  try {
    const raw = localStorage.getItem(DISMISSED_NOTIFICATION_STORAGE_KEY);
    if (!raw) return new Map();
    const parsed = JSON.parse(raw);

    const map = new Map<string, number>();
    const now = Math.floor(Date.now() / 1000);

    if (Array.isArray(parsed)) {
      // 兼容旧结构：string[]，升级时使用当前时间作为 dismissedAt
      parsed.forEach((key) => {
        if (typeof key === 'string') {
          map.set(key, now);
        }
      });
    } else if (parsed && typeof parsed === 'object') {
      Object.entries(parsed).forEach(([key, dismissedAt]) => {
        if (typeof dismissedAt === 'number' && Number.isFinite(dismissedAt)) {
          map.set(key, dismissedAt);
        }
      });
    }

    cleanupDismissedNotificationMap(map, now);
    return map;
  } catch {
    return new Map();
  }
}

function persistDismissedNotificationMap(dismissedMap: Map<string, number>) {
  try {
    const now = Math.floor(Date.now() / 1000);
    cleanupDismissedNotificationMap(dismissedMap, now);
    const payload = Object.fromEntries(dismissedMap.entries());
    localStorage.setItem(
      DISMISSED_NOTIFICATION_STORAGE_KEY,
      JSON.stringify(payload)
    );
  } catch {
    // ignore localStorage errors
  }
}

function extractTodoNotifications(
  todo: IHomeTodoResponse,
  orgId: string,
  memberId: string
): INotificationTodo[] {
  const items: INotificationTodo[] = [];

  (todo.perTodo || []).forEach((item) => {
    const isMonth = item.performance?.type === PerformanceType.Month;
    const stageCodeText = item.stageCode
      ? isMonth
        ? PerformanceMonthStageText[item.stageCode]
        : PerformanceStageText[item.stageCode]
      : '--';
    const isJumpDetail =
      item.stageCode === PerformanceStage.SelfEvaluation ||
      item.stageCode === PerformanceStage.Confirm;
    const url = isMonth
      ? `/org/${orgId}/performance/month`
      : isJumpDetail
      ? `/org/${orgId}/performance/my/detail?performance_id=${item.performance?.id}&stage=${item.stageCode}&account_id=${memberId}`
      : `/org/${orgId}/performance/home/todo?performance_id=${item.performance?.id}&stage=${item.stageCode}`;

    items.push({
      title: `【${stageCodeText}】待处理`,
      content: `请您尽快完成【${item.performance?.name}】的【${stageCodeText}】`,
      url,
      ctime: item?.lastTodoTime,
    });
  });

  (todo.leaveTodo || []).forEach((item) => {
    const userName = item.creator?.name || '';
    const leaveName =
      AuditBusinessCodeText[item.applyTypeCode as AuditBusinessCode] ||
      '请假申请';
    items.push({
      title: `【${userName}】的【请假申请】待处理`,
      content: `请您尽快处理【${userName}】的【${leaveName}】【${item.duration}天】申请`,
      url: `/org/${orgId}/leave/manage?tab=1`,
      ctime: item.ctime,
    });
  });

  (todo.outBusinessTodo || []).forEach((item) => {
    const userName = item.creator?.name || '';
    items.push({
      title: `【${userName}】的出差申请待处理`,
      content: `请您尽快处理【${userName}】的${
        item.destination ? `【${item.destination}】` : ''
      }【${item.duration}天】出差申请`,
      url: `/org/${orgId}/leave/out-business?tab=1`,
      ctime: item.ctime,
    });
  });

  (todo.outOfficialTodo || []).forEach((item) => {
    const userName = item.creator?.name || '';
    items.push({
      title: `【${userName}】的公出申请待处理`,
      content: `请您尽快处理【${userName}】的【${item.duration}天】公出申请`,
      url: `/org/${orgId}/leave/out-official?tab=1`,
      ctime: item.ctime,
    });
  });

  (todo.replaceCardTodo || []).forEach((item) => {
    const userName = item.applyer?.name || '';
    const date = item.recordDate
      ? new Date(item.recordDate * 1000).toLocaleDateString('zh-CN')
      : '';
    items.push({
      title: `【${userName}】的【${date}】补卡待处理`,
      content: `请您尽快处理【${userName}】的【${date}】的补卡申请`,
      url: `/org/${orgId}/attendance/my-attendance?tab=replaceCard`,
      ctime: item.ctime,
    });
  });

  (todo.entryTodo || []).forEach((item) => {
    const userName = item.name || '';
    items.push({
      title: `【${userName}】的入职申请待处理`,
      content: `请您尽快处理【${userName}】的入职申请`,
      url: `/org/${orgId}/people/entry/todo?account_id=${item.accountId}`,
      ctime: item.ctime,
    });
  });

  (todo.applyTodo || []).forEach((item) => {
    const userName = item.account?.name || '';
    let typeName = '';
    let url = '';
    if (item.applyType === 1) {
      url = `/org/${orgId}/people/staff-apply/resign/${item.auditId}`;
      typeName = '离职';
    } else if (item.applyType === 2) {
      url = `/org/${orgId}/people/staff-apply/regular/${item.auditId}`;
      typeName = '转正';
    } else if (item.applyType === 3) {
      url = `/org/${orgId}/people/staff-apply/transfer/${item.auditId}`;
      typeName = '调动';
    } else if (item.applyType === 4) {
      url = `/org/${orgId}/people/staff-apply/collect/${item.auditId}`;
      typeName = '信息采集';
    }
    items.push({
      title: `【${userName}】${typeName}的【${item.circleContent}】待处理`,
      content: `请您尽快处理【${userName}】${typeName}的【${item.circleContent}】`,
      url,
      ctime: item.auditUtime,
    });
  });

  (todo.overtimeTodo || []).forEach((item) => {
    const userName = item.creator?.name || '';
    items.push({
      title: `【${userName}】的加班申请待处理`,
      content: `请您尽快处理【${userName}】的加班申请`,
      url: `/org/${orgId}/attendance/overtime/detail/${item.auditId}`,
      ctime: item.ctime,
    });
  });

  (todo.recruitTodo || []).forEach((item) => {
    const userName = item.candidateAccountName || '';
    const stageMap: Record<string, string> = {
      screening: '简历筛选',
      interview: '面试评价',
      offer: 'offer 审批',
    };
    const routeMap: Record<string, string> = {
      screening: 'resume-screening',
      interview: 'interview',
      offer: 'candidate',
    };
    items.push({
      title: `【${stageMap[item.stage || '']}】待处理`,
      content: `请您尽快处理【${userName}】的【${stageMap[item.stage || '']}】`,
      url: `/org/${orgId}/recruitment/${
        routeMap[item.stage || '']
      }?account_id=${item.candidateAccountId || ''}&stage=${
        item.stage
      }&interview_id=${item.interviewId}`,
      ctime: item.utime || 0,
    });
  });

  (todo.positionApplyTodo || []).forEach((item) => {
    const userName = item.creator?.name || '';
    items.push({
      title: `【${userName}】的HC申请待处理`,
      content: `请您尽快处理【${userName}】的HC申请`,
      url: `/org/${orgId}/recruitment/hc/detail/${item.auditId}`,
      ctime: item.ctime,
    });
  });

  (todo.salaryIncreaseTodo || []).forEach((item) => {
    const userName = item.name || '';
    items.push({
      title: `【调薪申请】待处理`,
      content: `请您尽快处理【${userName}】的调薪申请`,
      url: `/org/${orgId}/salary/increase/apply/${item.auditId}`,
      ctime: item.ctime,
    });
  });

  (todo.trialTodo || []).forEach((item) => {
    const userName = item.name || '';
    items.push({
      title: `【结束试岗】待处理`,
      content: `【${userName}】的试岗期已到，请您尽快处理`,
      url: `/org/${orgId}/people/trial?key=${item.name}`,
      ctime: item.ctime,
    });
  });

  (todo.shiftApplyTodo || []).forEach((item) => {
    const userName = item?.shiftMember?.name || '';
    items.push({
      title: `【调班申请】待处理`,
      content: `请您尽快处理【${userName}】的调班申请`,
      url: `/org/${orgId}/attendance/shift-transfer/detail/${item.auditId}`,
      ctime: item.ctime,
    });
  });

  return items;
}

export const useTodoNotification = (orgId?: string) => {
  const { member } = useStore('user');
  const memberId = member?.memberId ? String(member.memberId) : '';
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const shownNotificationKeysRef = useRef<Set<string>>(new Set());
  const dismissedNotificationMapRef = useRef<Map<string, number>>(
    getDismissedNotificationMap()
  );

  const sendNotification = useCallback(
    (item: INotificationTodo, key: string) => {
      if (
        !('Notification' in window) ||
        Notification.permission !== 'granted' ||
        dismissedNotificationMapRef.current.has(key)
      ) {
        return;
      }

      const notification = new Notification(item.title, {
        body: item.content,
        tag: key,
        icon: LogoImg,
      });

      notification.onclick = () => {
        dismissedNotificationMapRef.current.set(
          key,
          Math.floor(Date.now() / 1000)
        );
        persistDismissedNotificationMap(dismissedNotificationMapRef.current);
        window.focus();
        window.location.href = item.url;
        notification.close();
      };
    },
    []
  );

  const pollTodos = useCallback(async () => {
    const perm = 'Notification' in window ? Notification.permission : 'N/A';
    const hasNotif = 'Notification' in window;
    // 诊断：测试环境不打印时，检查这些值是否导致提前 return
    if (!orgId) {
      console.warn('[useTodoNotification] 提前返回: orgId 为空', {
        orgId,
        perm,
        hasNotif,
      });
      return;
    }
    if (!hasNotif || perm !== 'granted') {
      console.warn('[useTodoNotification] 提前返回: 通知权限未授予', {
        orgId,
        perm,
        hasNotif,
      });
      return;
    }

    try {
      const pollTime = Math.floor(Date.now() / 1000);
      cleanupDismissedNotificationMap(
        dismissedNotificationMapRef.current,
        pollTime
      );
      persistDismissedNotificationMap(dismissedNotificationMapRef.current);
      // TODO: 测试完成后删除 mock，恢复 apiHomeTodoList 调用
      // const todoData: IHomeTodoResponse = {
      //   leaveTodo: [
      //     {
      //       creator: { name: '张三' } as any,
      //       duration: 3,
      //       ctime: pollTime - 60,
      //     } as any,
      //   ],
      //   overtimeTodo: [
      //     {
      //       creator: { name: '李四' } as any,
      //       auditId: 1,
      //       ctime: pollTime - 120,
      //     } as any,
      //   ],
      //   recruitTodo: [
      //     {
      //       candidateAccountName: '王五',
      //       candidateAccountId: 100,
      //       stage: 'interview',
      //       interviewId: 1,
      //       ctime: pollTime - 30,
      //     },
      //   ],
      //   entryTodo: [
      //     {
      //       todoId: 1,
      //       accountId: 200,
      //       name: '赵六',
      //       ctime: pollTime - 200,
      //     },
      //   ],
      //   shiftApplyTodo: [
      //     {
      //       shiftMember: { name: '钱七' } as any,
      //       auditId: 2,
      //       ctime: pollTime - 600,
      //     } as any,
      //   ],
      // };
      const todoData = await apiHomeTodoList();
      if (!todoData) return;

      const items = extractTodoNotifications(todoData, orgId, memberId);
      const threshold = pollTime - TIME_WINDOW;

      const newItems = items.filter(
        (item) =>
          item.ctime && item.ctime >= threshold && item.ctime <= pollTime
      );

      newItems.forEach((item) => {
        const key = buildNotificationKey(item);
        if (
          shownNotificationKeysRef.current.has(key) ||
          dismissedNotificationMapRef.current.has(key)
        ) {
          return;
        }
        shownNotificationKeysRef.current.add(key);
        sendNotification(item, key);
      });
    } catch (e) {
      console.error('待办通知轮询失败:', e);
    }
  }, [memberId, orgId, sendNotification]);

  useEffect(() => {
    if (!orgId) return;

    const initNotification = async () => {
      if (!('Notification' in window)) return;
      if (Notification.permission === 'default') {
        const perm = await Notification.requestPermission();
        // 用户授权后立即轮询一次，否则需等 10 分钟
        if (perm === 'granted') pollTodos();
      } else if (Notification.permission === 'granted') {
        pollTodos();
      }
    };
    initNotification();
    timerRef.current = setInterval(pollTodos, POLL_INTERVAL);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [orgId, pollTodos]);
};
